import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { JournalFormComponent } from '../journal-form/journal-form.component';
import { Packet } from '@escrowtab/packets-api-client';
import { ProfileDto } from '@escrowtab/v2profile-api-client';
import { SignerFormComponent } from '../signer-form/signer-form.component';
import {
  AddEtJournalSignerDto,
  AddPacketJournalDto,
  EtjournalPacketEntry,
  EtJournalSigner,
  GenerateEjournalDto,
  PatchEtJournalSignerDto,
} from '@et/typings';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { JournalPacketCardComponent } from '../journal-packet-card/journal-packet-card.component';
import { JournalSignerCardComponent } from '../journal-signer-card/journal-signer-card.component';
import { EjournalApiService } from '@et/api';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { catchError, finalize } from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';

@Component({
  selector: 'et-journal-journal-generator',
  imports: [
    PresentationalAtomsModule,
    JournalFormComponent,
    SignerFormComponent,
    MatDialogClose,
    JournalPacketCardComponent,
    JournalSignerCardComponent,
  ],
  templateUrl: './journal-generator.component.html',
  styleUrl: './journal-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalGeneratorComponent implements OnInit {
  @Input({ required: true }) packet!: Packet;
  @Input({ required: true }) profile!: ProfileDto;

  @ViewChild(JournalFormComponent) journalFormComponent!: JournalFormComponent;
  @ViewChild(SignerFormComponent) signerFormComponent?: SignerFormComponent;

  private ejournalApiService = inject(EjournalApiService);
  private dialogRef = inject(MatDialogRef<JournalGeneratorComponent>);
  private ejournalService = inject(EjournalService);

  isSaving = signal(false);
  error = signal<string | null>(null);

  step = signal<number>(1);

  signers: Array<EtJournalSigner | null> = [];
  info: EtjournalPacketEntry | null = null;

  ipAddress = this.ejournalService.ip;

  /** Each packet signer is edited on its own step (between journal and confirm). */
  signerCount(): number {
    return this.signers.length;
  }

  /** Journal + one step per signer + confirm. */
  totalSteps(): number {
    return 2 + this.signerCount();
  }

  confirmStep(): number {
    return this.totalSteps();
  }

  isSignerStep(step: number): boolean {
    const n = this.signerCount();
    return n > 0 && step >= 2 && step <= 1 + n;
  }

  currentSignerIndex(): number {
    return this.step() - 2;
  }

  ngOnInit(): void {
    // Add signers from packet
    this.packet.signers?.forEach(() => this.signers.push(null));
  }

  onNextStep(): void {
    const currentStep = this.step();
    const max = this.totalSteps();
    if (currentStep >= max) {
      return;
    }

    const component = this.getStepComponent(currentStep);
    if (!component) {
      this.step.set(currentStep + 1);
    } else if (component.form.invalid) {
      component.showValidationErrors();
    } else {
      component.onSubmit();

      // Reset form for next signer step
      if (this.isSignerStep(currentStep + 1)) {
        this.signerFormComponent?.reset();
      }

      this.step.set(currentStep + 1);
    }
  }

  saveInfo(payload: EtjournalPacketEntry | AddPacketJournalDto) {
    delete (payload as any).id;
    this.info = { ...payload } as unknown as EtjournalPacketEntry;
  }

  saveSigner(
    step: number,
    payload: Partial<AddEtJournalSignerDto | PatchEtJournalSignerDto>,
  ) {
    const signerIndex = step - 2;
    this.signers[signerIndex] = {
      ...payload,
    } as unknown as EtJournalSigner;
  }

  onPreviousStep(): void {
    const step = this.step();
    if (step <= 1) {
      return;
    }

    // If signer step save signer progress
    const component = this.getStepComponent(step);
    if (
      this.isSignerStep(step) &&
      component &&
      component instanceof SignerFormComponent
    ) {
      component.onSubmitWithoutValidation();
    }

    this.step.set(step - 1);
  }

  onSave() {
    this.isSaving.set(true);
    const journalPayload: AddPacketJournalDto = this
      .info as AddPacketJournalDto;
    const signersPayload: AddEtJournalSignerDto[] = this.signers.map(
      (signer) => {
        delete (signer as any).id;
        delete (signer as any).signatures;
        return signer as unknown as AddEtJournalSignerDto;
      },
    );

    const payload: GenerateEjournalDto = {
      newJournal: journalPayload,
      newSigners: signersPayload,
    };

    this.ejournalApiService
      .generateEjournal(payload)
      .pipe(
        finalize(() => {
          this.isSaving.set(false);
        }),
        catchError((error) => {
          this.error.set('Failed to generate eJournal');
          throw error;
        }),
      )
      .subscribe((journal) => this.dialogRef.close(journal));
  }

  private getStepComponent(
    step: number,
  ): JournalFormComponent | SignerFormComponent | undefined {
    if (step === 1) {
      return this.journalFormComponent;
    }
    if (this.isSignerStep(step)) {
      return this.signerFormComponent;
    }
    return undefined;
  }
}
